import { FC, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchCurrentUser, MeResponse } from '../api/auth';
import CustomerVehicleSelection, {
  type CustomerVehicleSelectionHandle,
  type CustomerVehicleSelectionSnapshot,
} from './orderManagement/components/CustomerVehicleSelection';
import { fetchProductVariants, fetchProductVariant, type ProductVariant } from '../api/productVariants';
import { createAppointment } from '../api/appointments';
import {
  fetchBrandRelatedVariants,
  fetchModelRelatedVariants,
  type RelatedVariant,
} from '../api/vehicleRelatedVariants';
import { fetchCustomerVehicleNextDueOrderItems } from '../api/dashboard';

const NewAppointmentPage: FC = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const selectionRef = useRef<CustomerVehicleSelectionHandle | null>(null);
  const [selectionSnapshot, setSelectionSnapshot] =
    useState<CustomerVehicleSelectionSnapshot | null>(null);

  const [servicesSearch, setServicesSearch] = useState('');
  const [serviceOptions, setServiceOptions] = useState<ProductVariant[]>([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [servicesError, setServicesError] = useState<string | null>(null);
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [selectedServices, setSelectedServices] = useState<ProductVariant[]>(
    [],
  );
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [relatedVariants, setRelatedVariants] = useState<ProductVariant[]>([]);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [relatedError, setRelatedError] = useState<string | null>(null);
  const [isRelatedModalOpen, setIsRelatedModalOpen] = useState(false);
  const [isUpcomingModalOpen, setIsUpcomingModalOpen] = useState(false);
  const [upcomingLoading, setUpcomingLoading] = useState(false);
  const [upcomingError, setUpcomingError] = useState<string | null>(null);
  const [upcomingRows, setUpcomingRows] = useState<
    import('../api/dashboard').CustomerVehicleNextDueRow[]
  >([]);
  const [scheduledAt, setScheduledAt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    let mounted = true;
    fetchCurrentUser()
      .then((resp) => {
        if (mounted) {
          setMe(resp);
          setLoadingUser(false);
        }
      })
      .catch(() => {
        if (mounted) {
          setMe(null);
          setLoadingUser(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  // Load related variants for selected vehicle brand/model
  useEffect(() => {
    if (!selectionSnapshot?.vehicleBrandId && !selectionSnapshot?.vehicleModelId) {
      setRelatedVariants([]);
      setRelatedError(null);
      return;
    }

    let ignore = false;
    const load = async () => {
      setRelatedLoading(true);
      setRelatedError(null);
      try {
        const results: RelatedVariant[] = [];
        if (selectionSnapshot.vehicleBrandId) {
          const brandRelated = await fetchBrandRelatedVariants(
            selectionSnapshot.vehicleBrandId,
          );
          results.push(...brandRelated);
        }
        if (selectionSnapshot.vehicleModelId) {
          const modelRelated = await fetchModelRelatedVariants(
            selectionSnapshot.vehicleModelId,
          );
          results.push(...modelRelated);
        }
        if (!ignore) {
          const uniqueById: Record<string, ProductVariant> = {};
          results.forEach((rel) => {
            uniqueById[rel.productVariant.id] = rel.productVariant;
          });
          setRelatedVariants(Object.values(uniqueById));
        }
      } catch (err) {
        if (!ignore) {
          setRelatedError(
            err instanceof Error
              ? err.message
              : 'Failed to load related products.',
          );
          setRelatedVariants([]);
        }
      } finally {
        if (!ignore) setRelatedLoading(false);
      }
    };
    void load();

    return () => {
      ignore = true;
    };
  }, [selectionSnapshot?.vehicleBrandId, selectionSnapshot?.vehicleModelId]);

  useEffect(() => {
    if (!servicesSearch.trim()) {
      setServiceOptions([]);
      setServicesError(null);
      setShowServicesDropdown(false);
      return;
    }
    let ignore = false;
    const timeoutId = window.setTimeout(async () => {
      setServicesLoading(true);
      setServicesError(null);
      try {
        const resp = await fetchProductVariants({
          page: 1,
          limit: 20,
          search: servicesSearch.trim(),
          isActive: true,
        });
        if (!ignore) {
          setServiceOptions(resp.data);
          setShowServicesDropdown(true);
        }
      } catch (err) {
        if (!ignore) {
          setServicesError(
            err instanceof Error ? err.message : 'Failed to search services.',
          );
          setServiceOptions([]);
        }
      } finally {
        if (!ignore) setServicesLoading(false);
      }
    }, 300);
    return () => {
      ignore = true;
      window.clearTimeout(timeoutId);
    };
  }, [servicesSearch]);

  const toggleService = (variant: ProductVariant) => {
    setSelectedServiceIds((prevIds) =>
      prevIds.includes(variant.id)
        ? prevIds.filter((x) => x !== variant.id)
        : [...prevIds, variant.id],
    );
    setSelectedServices((prev) => {
      const exists = prev.find((v) => v.id === variant.id);
      if (exists) {
        return prev.filter((v) => v.id !== variant.id);
      }
      return [...prev, variant];
    });
  };

  const removeService = (id: string) => {
    setSelectedServiceIds((prevIds) => prevIds.filter((x) => x !== id));
    setSelectedServices((prev) => prev.filter((v) => v.id !== id));
  };

  const handleCreateAppointment = async () => {
    if (!selectionRef.current) return;
    const errors = selectionRef.current.validate();
    const snapshot = selectionRef.current.getDetails();

    const missing: string[] = [...errors];
    if (!snapshot.locationId) {
      missing.push('Location is required.');
    }
    if (!scheduledAt) {
      missing.push('Appointment date & time is required.');
    }

    if (missing.length > 0) {
      setSubmitError(missing.join(' '));
      return;
    }
    setSubmitError(null);

    setSubmitting(true);
    try {
      await createAppointment({
        customerId: snapshot.customer!.id,
        vehicleId: snapshot.customerVehicleId,
        locationId: snapshot.locationId!,
        advisorId: me?.user?.id ?? undefined,
        serviceIds: selectedServiceIds.length ? selectedServiceIds : undefined,
        scheduledAt: new Date(scheduledAt).toISOString(),
        notes: notes.trim() || null,
      });
      navigate('/appointments');
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : 'Failed to create appointment.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-[96rem] rounded-[28px] border border-slate-200/70 bg-white/95 p-10 shadow-[0_20px_45px_-32px_rgba(37,40,85,0.55)]">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-800">
            New Appointment
          </h1>
          <p className="max-w-2xl text-sm text-slate-500">
            Select customer, vehicle, and branch, then optionally add service
            variants and pick the appointment date & time.
          </p>
        </div>
      </div>

      <section className="space-y-6">
        <CustomerVehicleSelection
          ref={selectionRef}
          currentUser={me?.user ?? null}
          loadingUser={loadingUser}
          showProceedButton={false}
          onChange={(snapshot) => setSelectionSnapshot(snapshot)}
        />

        <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
          <h2 className="text-sm font-semibold text-slate-700">
            Appointment details
          </h2>
          <div className="mt-3 grid gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-1">
              <label
                htmlFor="scheduledAt"
                className="text-[11px] font-semibold uppercase tracking-wide text-slate-400"
              >
                Appointment date &amp; time <span className="text-red-500">*</span>
              </label>
              <input
                id="scheduledAt"
                type="datetime-local"
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label
                htmlFor="notes"
                className="text-[11px] font-semibold uppercase tracking-wide text-slate-400"
              >
                Notes
              </label>
              <textarea
                id="notes"
                rows={3}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="Optional notes about this appointment…"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-slate-700">
                Services &amp; follow-ups
              </h2>
              <p className="text-xs text-slate-500">
                Search or pick from suggestions, then add to this appointment.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={
                  !selectionSnapshot?.customer ||
                  !selectionSnapshot?.customerVehicleId
                }
                onClick={async () => {
                  if (
                    !selectionSnapshot?.customer ||
                    !selectionSnapshot.customerVehicleId
                  )
                    return;
                  setIsUpcomingModalOpen(true);
                  setUpcomingLoading(true);
                  setUpcomingError(null);
                  try {
                    const rows = await fetchCustomerVehicleNextDueOrderItems(
                      selectionSnapshot.customer.id,
                      selectionSnapshot.customerVehicleId,
                      10,
                    );
                    setUpcomingRows(rows);
                  } catch (err) {
                    setUpcomingError(
                      err instanceof Error
                        ? err.message
                        : 'Failed to load upcoming activities.',
                    );
                    setUpcomingRows([]);
                  } finally {
                    setUpcomingLoading(false);
                  }
                }}
              >
                Upcoming Activities
              </button>
              <button
                type="button"
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={
                  !selectionSnapshot?.customerVehicleId ||
                  relatedLoading ||
                  (!!selectionSnapshot?.customerVehicleId &&
                    relatedVariants.length === 0 &&
                    !relatedError)
                }
                onClick={() => setIsRelatedModalOpen(true)}
              >
                Related products &amp; services
              </button>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-3 md:flex-row">
            <div className="flex-1">
              <label
                htmlFor="servicesSearch"
                className="text-[11px] font-semibold uppercase tracking-wide text-slate-400"
              >
                Service variants (optional)
              </label>
              <input
                id="servicesSearch"
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="Search services by name, code, or ERP ID…"
                value={servicesSearch}
                onChange={(e) => setServicesSearch(e.target.value)}
              />
              {servicesError ? (
                <p className="mt-1 text-xs text-danger">{servicesError}</p>
              ) : null}
              <div className="mt-1 flex items-center justify-between text-[11px] text-slate-500">
                {servicesLoading ? (
                  <span>Searching services…</span>
                ) : servicesSearch && serviceOptions.length > 0 ? (
                  <span>Click to add/remove services.</span>
                ) : (
                  <span>Start typing to search services.</span>
                )}
                {showServicesDropdown && (
                  <button
                    type="button"
                    className="text-xs font-semibold text-slate-500 hover:text-slate-700"
                    onClick={() => {
                      setShowServicesDropdown(false);
                      setServiceOptions([]);
                    }}
                  >
                    Close
                  </button>
                )}
              </div>
              {showServicesDropdown &&
                servicesSearch &&
                serviceOptions.length > 0 ? (
                <div className="mt-2 max-h-64 overflow-auto rounded-xl border border-slate-200 bg-white text-sm shadow-lg">
                  {serviceOptions.map((variant) => {
                    const checked = selectedServiceIds.includes(variant.id);
                    const isService = variant.isCreatedByWorkshopManagement;
                    return (
                      <button
                        key={variant.id}
                        type="button"
                        className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-primary/5"
                        onClick={() => toggleService(variant)}
                      >
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-700">
                              {variant.name}
                            </span>
                            <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                              {isService ? 'Service' : 'Product'}
                            </span>
                          </div>
                          <span className="text-xs text-slate-500">
                            {variant.variantCode || variant.erpId}
                          </span>
                        </div>
                        <span
                          className={`inline-flex h-4 w-4 items-center justify-center rounded border text-[10px] ${checked
                            ? 'border-primary bg-primary text-white'
                            : 'border-slate-300 bg-white text-slate-500'
                            }`}
                        >
                          {checked ? '✓' : ''}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>

            {selectedServices.length > 0 ? (
              <div className="flex-1 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Added services
                </h3>
                <ul className="mt-2 divide-y divide-slate-100">
                  {selectedServices.map((variant) => (
                    <li
                      key={variant.id}
                      className="flex items-center justify-between py-2 text-sm"
                    >
                      <div>
                        <div className="font-medium text-slate-800">
                          {variant.name}
                        </div>
                        <div className="text-xs text-slate-500">
                          {variant.variantCode || variant.erpId}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="text-xs font-semibold text-danger hover:underline"
                        onClick={() => removeService(variant.id)}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </div>

        {submitError ? (
          <div className="rounded-xl border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
            {submitError}
          </div>
        ) : null}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
            onClick={() => navigate('/appointments')}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-primary/60"
            onClick={handleCreateAppointment}
            disabled={submitting}
          >
            {submitting ? 'Creating…' : 'Create appointment'}
          </button>
        </div>
      </section>

      {isRelatedModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-5 shadow-xl">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-slate-800">
                  Related products &amp; services
                </h2>
                <p className="text-xs text-slate-500">
                  Suggestions based on this vehicle&apos;s brand and model. Click
                  &quot;Add&quot; to include them in this appointment.
                </p>
              </div>
              <button
                type="button"
                className="rounded-full border border-slate-200 p-1 text-slate-500 hover:bg-slate-50"
                onClick={() => setIsRelatedModalOpen(false)}
              >
                ✕
              </button>
            </div>

            {relatedError ? (
              <p className="text-xs text-danger">{relatedError}</p>
            ) : relatedLoading ? (
              <p className="text-xs text-slate-500">
                Loading related products…
              </p>
            ) : relatedVariants.length === 0 ? (
              <p className="text-xs text-slate-500">
                No related products configured for this vehicle.
              </p>
            ) : (
              <div className="mt-2 max-h-80 overflow-auto rounded-xl border border-slate-200">
                <table className="min-w-full text-left text-sm text-slate-700">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-2">Name</th>
                      <th className="px-4 py-2">Type</th>
                      <th className="px-4 py-2">Code / ERP</th>
                      <th className="px-4 py-2 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {relatedVariants.map((variant) => {
                      const isService = variant.isCreatedByWorkshopManagement;
                      const checked = selectedServiceIds.includes(variant.id);
                      return (
                        <tr key={variant.id} className="even:bg-slate-50/60">
                          <td className="px-4 py-2 font-medium">
                            {variant.name}
                          </td>
                          <td className="px-4 py-2">
                            <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                              {isService ? 'Service' : 'Product'}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-xs text-slate-500">
                            {variant.variantCode || variant.erpId}
                          </td>
                          <td className="px-4 py-2 text-right">
                            <button
                              type="button"
                              className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${checked
                                ? 'border border-slate-300 text-slate-700 hover:bg-slate-50'
                                : 'bg-primary text-white hover:bg-primary/90'
                                }`}
                              onClick={() => toggleService(variant)}
                            >
                              {checked ? 'Remove' : 'Add'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                className="rounded-lg border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                onClick={() => setIsRelatedModalOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {isUpcomingModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-5 shadow-xl">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-slate-800">
                  Upcoming Activities
                </h2>
                <p className="text-xs text-slate-500">
                  Upcoming service/items due for this customer and vehicle.
                </p>
              </div>
              <button
                type="button"
                className="rounded-full border border-slate-200 p-1 text-slate-500 hover:bg-slate-50"
                onClick={() => setIsUpcomingModalOpen(false)}
              >
                ✕
              </button>
            </div>

            {upcomingError ? (
              <p className="text-xs text-danger">{upcomingError}</p>
            ) : upcomingLoading ? (
              <p className="text-xs text-slate-500">Loading upcoming activites...</p>
            ) : upcomingRows.length === 0 ? (
              <p className="text-xs text-slate-500">
                No upcoming activities found for this customer and vehicle.
              </p>
            ) : (
              <div className="mt-2 max-h-80 overflow-auto rounded-xl border border-slate-200">
                <table className="min-w-full text-left text-sm text-slate-700">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-2">Order #</th>
                      <th className="px-4 py-2">Item</th>
                      <th className="px-4 py-2">Due date</th>
                      <th className="px-4 py-2 text-right">Days left</th>
                      <th className="px-4 py-2 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upcomingRows.map((row) => {
                      const isSelected = selectedServiceIds.includes(
                        row.productVariantId,
                      );
                      return (
                        <tr
                          key={`${row.orderId}-${row.productVariantId}`}
                          className="even:bg-slate-50/60"
                        >
                          <td className="px-4 py-2 text-xs">
                            {row.orderNumber ?? '—'}
                          </td>
                          <td className="px-4 py-2 text-xs">
                            {row.productName ?? 'Item'}
                          </td>
                          <td className="px-4 py-2 text-xs">
                            {new Date(row.dueDate).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-2 text-right text-xs">
                            {row.daysLeft}
                          </td>
                          <td className="px-4 py-2 text-right">
                            <button
                              type="button"
                              className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${isSelected
                                ? 'border border-slate-300 text-slate-700 hover:bg-slate-50'
                                : 'bg-primary text-white hover:bg-primary/90'
                                }`}
                              onClick={async () => {
                                try {
                                  const variant = await fetchProductVariant(
                                    row.productVariantId,
                                  );
                                  toggleService(variant);
                                } catch (err) {
                                  setUpcomingError(
                                    err instanceof Error
                                      ? err.message
                                      : 'Failed to load variant details.',
                                  );
                                }
                              }}
                            >
                              {isSelected ? 'Remove' : 'Add'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                className="rounded-lg border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                onClick={() => setIsUpcomingModalOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewAppointmentPage;
